import { Component, TemplateRef, viewChild, inject, ChangeDetectionStrategy } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { Router, RouterLinkActive, RouterLink, RouterOutlet } from "@angular/router";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { DangerSourcesService } from "app/danger-sources/danger-sources.service";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { BsModalService } from "ngx-bootstrap/modal";
import { BsModalRef } from "ngx-bootstrap/modal";

import { environment } from "../../environments/environment";
import { RegionConfiguration } from "../models/region-configuration.model";
import { AuthenticationService } from "../providers/authentication-service/authentication.service";
import { BulletinsService } from "../providers/bulletins-service/bulletins.service";
import { ConstantsService } from "../providers/constants-service/constants.service";
import { LocalStorageService } from "../providers/local-storage-service/local-storage.service";
import { RegionsService } from "../providers/regions-service/regions.service";
import { NgxMousetrapDirective } from "../shared/mousetrap-directive";

@Component({
  selector: "app-dashboard",
  templateUrl: "./full-layout.component.html",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLinkActive, RouterLink, BsDropdownModule, RouterOutlet, TranslatePipe, NgxMousetrapDirective],
})
export class FullLayoutComponent {
  translateService = inject(TranslateService);
  authenticationService = inject(AuthenticationService);
  bulletinsService = inject(BulletinsService);
  dangerSourcesService = inject(DangerSourcesService);
  regionsService = inject(RegionsService);
  constantsService = inject(ConstantsService);
  router = inject(Router);
  localStorageService = inject(LocalStorageService);
  private modalService = inject(BsModalService);
  private sanitizer = inject(DomSanitizer);

  public isSidebarOpen = false;

  public tmpRegion: RegionConfiguration;

  public changeRegionModalRef: BsModalRef;
  readonly changeRegionTemplate = viewChild<TemplateRef<unknown>>("changeRegionTemplate");

  public config = {
    animated: false,
    keyboard: true,
    class: "modal-sm",
  };

  get environment() {
    return environment;
  }

  get isNavbarShown(): boolean {
    return this.authenticationService.isUserLoggedIn() || !this.router.url.startsWith("/modelling");
  }

  getStyle() {
    const color = this.localStorageService.isTrainingEnabled ? "#a6d96a" : environment.headerBgColor;
    const style = `background-color: ${color}`;
    return this.sanitizer.bypassSecurityTrustStyle(style);
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  public logout() {
    this.authenticationService.logout();
  }

  changeRegion(region: RegionConfiguration) {
    if (!this.authenticationService.getActiveRegionId().startsWith(region.id)) {
      if (
        this.router.url.startsWith("/bulletins/") &&
        !this.router.url.endsWith("/publication") &&
        this.bulletinsService.getIsEditable()
      ) {
        this.tmpRegion = region;
        this.openChangeRegionModal(this.changeRegionTemplate());
      } else {
        this.change(region);
      }
    }
  }

  openChangeRegionModal(template: TemplateRef<unknown>) {
    this.changeRegionModalRef = this.modalService.show(template, this.config);
  }

  changeRegionModalConfirm(): void {
    this.changeRegionModalRef.hide();
    this.change(this.tmpRegion);
    this.tmpRegion = undefined;
  }

  changeRegionModalDecline(): void {
    this.changeRegionModalRef.hide();
  }

  focusMap() {
    const mapElement = document.getElementById("map") ?? document.getElementById("observationsMap");
    if (mapElement) {
      mapElement.focus();
    }
  }

  private change(region: RegionConfiguration) {
    this.authenticationService.setActiveRegion(region);
    this.bulletinsService.loadStatus();
    this.dangerSourcesService.loadStatus();
    if (
      (this.router.url.startsWith("/modelling/geosphere") && !region.enableModelling) ||
      (this.router.url.startsWith("/observations") && !region.enableObservations) ||
      (this.router.url.startsWith("/icon") && !region.enableIcon)
    ) {
      this.router.navigate(["/bulletins"]);
    }
  }
}
